// ============================================================================
// ACTIONS CONFIGURATION - Actions menu for table rows (similar to batch/PO quick)
// ============================================================================

/**
 * Get actions configuration for a specific tab
 * This maintains the logic structure even without data
 * 
 * @param {number|string} tabId - Tab ID or tab name
 * @param {Object} options - Optional configuration
 * @param {Object} options.featuresAccess - Feature access object for filtering actions
 * @param {Function} options.onActionClick - Optional callback when action is clicked
 * @returns {Array} Array of action objects
 * 
 * Action object structure:
 * {
 *   label: "Action Label",           // Display label in menu
 *   title: "Dialog Title",            // Title for dialog/QuickView
 *   component: <Component />,         // Optional: React component for dialog
 *   isQuickView: true,                // Optional: Flag for QuickView (full-screen dialog)
 *   onClick: (row) => {},             // Optional: Custom click handler
 *   featureAccess: "featureKey",      // Optional: Feature access key for filtering
 *   quickViewComponent: <Component />, // Optional: QuickView component
 * }
 */
export const getTabActionsConfig = (tabId, options = {}) => {
  const { featuresAccess = {}, onActionClick = null } = options;
  
  // Default actions that can be applied to any tab
  // These are placeholders - actual actions will be defined per tab as needed
  const defaultActions = [
    {
      label: "View Details",
      title: "View Details",
      isQuickView: true,
      featureAccess: "viewDetails",
      // quickViewComponent will be passed when rendering
    },
    {
      label: "Edit",
      title: "Edit",
      onClick: (row) => {
        console.log("Edit action clicked for row:", row);
        // Custom handler can be provided
        if (onActionClick) {
          onActionClick({ label: "Edit", title: "Edit" }, row);
        }
      },
      featureAccess: "edit",
    },
  ];

  // Tab-specific actions configuration
  // This structure allows different tabs to have different actions
  const tabSpecificActions = {
    // Materials & Consumption tab (ID: 1)
    1: [
      {
        label: "View Consumption Details",
        title: "View Consumption Details",
        isQuickView: true,
        featureAccess: "viewConsumption",
      },
      {
        label: "View Material Details",
        title: "View Material Details",
        isQuickView: true,
        featureAccess: "viewMaterialDetails",
      },
    ],
    // CPPs tab (ID: 2)
    2: [
      {
        label: "View CPP Details",
        title: "View CPP Details",
        isQuickView: true,
        featureAccess: "viewCPPDetails",
      },
      {
        label: "View Time Series Data",
        title: "View Time Series Data",
        isQuickView: true,
        featureAccess: "viewTimeSeries",
      },
    ],
    // In-Process Tests tab (ID: 3)
    3: [
      {
        label: "View Test Details",
        title: "View Test Details",
        isQuickView: true,
        featureAccess: "viewTestDetails",
      },
      {
        label: "View COA",
        title: "View COA",
        isQuickView: true,
        featureAccess: "viewCOA",
      },
    ],
    // Information Only Tests tab (ID: 4)
    4: [
      {
        label: "View Test Details",
        title: "View Test Details",
        isQuickView: true,
        featureAccess: "viewTestDetails",
      },
    ],
    // Product Release Tests tab (ID: 5)
    5: [
      {
        label: "View Release Test Details",
        title: "View Release Test Details",
        isQuickView: true,
        featureAccess: "viewReleaseTestDetails",
      },
      {
        label: "View COA",
        title: "View COA",
        isQuickView: true,
        featureAccess: "viewCOA",
      },
    ],
    // CQAs tab (ID: 6)
    6: [
      {
        label: "View CQA Details",
        title: "View CQA Details",
        isQuickView: true,
        featureAccess: "viewCQADetails",
      },
    ],
    // Deviations tab (ID: 7)
    7: [
      {
        label: "View Deviation Details",
        title: "View Deviation Details",
        isQuickView: true,
        featureAccess: "viewDeviationDetails",
      },
      {
        label: "View Deviation Reports",
        title: "View Deviation Reports",
        isQuickView: true,
        featureAccess: "viewDeviationReports",
      },
    ],
    // Change Controls tab (ID: 8)
    8: [
      {
        label: "View Change Control Details",
        title: "View Change Control Details",
        isQuickView: true,
        featureAccess: "viewChangeControlDetails",
      },
      {
        label: "View Change Control Report",
        title: "View Change Control Report",
        isQuickView: true,
        featureAccess: "viewChangeControlReport",
      },
    ],
    // Cycle Time tab (ID: 9)
    9: [
      {
        label: "View Cycle Time Details",
        title: "View Cycle Time Details",
        isQuickView: true,
        featureAccess: "viewCycleTimeDetails",
      },
    ],
    // Equipment tab (ID: 10)
    10: [
      {
        label: "View Equipment Details",
        title: "View Equipment Details",
        isQuickView: true,
        featureAccess: "viewEquipmentDetails",
      },
    ],
    // Yield tab (ID: 11)
    11: [
      {
        label: "View Yield Details",
        title: "View Yield Details",
        isQuickView: true,
        featureAccess: "viewYieldDetails",
      },
      {
        label: "View Materials Consumption",
        title: "View Materials Consumption",
        isQuickView: true,
        featureAccess: "viewMaterialsConsumption",
      },
    ],
    // Documents tab (ID: 12)
    12: [
      {
        label: "View Document",
        title: "View Document",
        isQuickView: true,
        featureAccess: "viewDocument",
      },
      {
        label: "Download Document",
        title: "Download Document",
        onClick: (row) => {
          console.log("Download document for row:", row);
          if (onActionClick) {
            onActionClick({ label: "Download Document", title: "Download Document" }, row);
          }
        },
        featureAccess: "downloadDocument",
      },
    ],
  };

  // Get tab-specific actions or use default
  const tabActions = tabSpecificActions[tabId] || defaultActions;

  // Filter actions based on feature access if provided
  if (featuresAccess && Object.keys(featuresAccess).length > 0) {
    return tabActions.filter((action) => {
      // If action has featureAccess key, check if user has access
      if (action.featureAccess) {
        // If featureAccess is explicitly false, hide the action
        // Otherwise, show it (true or undefined)
        return featuresAccess[action.featureAccess] !== false;
      }
      // If no featureAccess specified, show by default
      return true;
    });
  }

  return tabActions;
};

/**
 * Get actions configuration for all tabs
 * Useful for bulk configuration or initialization
 * 
 * @param {Object} options - Optional configuration
 * @returns {Object} Object with tab IDs as keys and actions arrays as values
 */
export const getAllTabsActionsConfig = (options = {}) => {
  const allTabs = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
  const actionsConfig = {};
  
  allTabs.forEach((tabId) => {
    actionsConfig[tabId] = getTabActionsConfig(tabId, options);
  });
  
  return actionsConfig;
};

